import {useState, useCallback, useRef} from "react"
import {calculateMovementLimits} from "../utils/calculationsUtils"
import {CONSTANTS} from "../constants/constants"
import type {CropperHookReturn} from "../types/types"

interface UseDragAndDropProps {
    cropper: CropperHookReturn
}

export const useDragAndDrop = ({cropper}: UseDragAndDropProps) => {
    const [isLocalDragging, setIsLocalDragging] = useState(false)
    const dragStartRef = useRef<{x: number; y: number} | null>(null)
    const rafIdRef = useRef<number | null>(null)

    const handleDragStart = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (!cropper || cropper.imageState.status !== "loaded") return

            const rect = e.currentTarget.getBoundingClientRect()

            dragStartRef.current = {
                x: e.clientX - rect.left - cropper.imageState.position.x,
                y: e.clientY - rect.top - cropper.imageState.position.y,
            }

            setIsLocalDragging(true)

            cropper.setCropState({
                status: "dragging",
                isDragging: true,
                dragStart: dragStartRef.current,
            })
        },
        [cropper]
    )

    const handleDrag = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (
                !cropper ||
                !dragStartRef.current ||
                cropper.imageState.status !== "loaded" ||
                !isLocalDragging
            )
                return

            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current)
            }

            const rect = e.currentTarget.getBoundingClientRect()
            const mouseX = e.clientX - rect.left
            const mouseY = e.clientY - rect.top

            let newPosition

            if (cropper.mode === CONSTANTS.EDITOR_MODES.TILE) {
                newPosition = {
                    x: mouseX - dragStartRef.current.x,
                    y: mouseY - dragStartRef.current.y,
                }
            } else {
                const movementLimits = calculateMovementLimits(
                    cropper.imageState.dimensions,
                    cropper.cropState.dimensions,
                    cropper.imageState.zoom
                )

                newPosition = {
                    x: Math.min(
                        movementLimits.maxX,
                        Math.max(
                            movementLimits.minX,
                            mouseX - dragStartRef.current.x
                        )
                    ),
                    y: Math.min(
                        movementLimits.maxY,
                        Math.max(
                            movementLimits.minY,
                            mouseY - dragStartRef.current.y
                        )
                    ),
                }
            }

            const zoomFactor = cropper.imageState.zoom / 100
            const dimensions = {
                width: cropper.imageState.dimensions.width * zoomFactor,
                height: cropper.imageState.dimensions.height * zoomFactor,
            }

            rafIdRef.current = requestAnimationFrame(() => {
                cropper.setRenderData({
                    position: newPosition,
                    dimensions,
                })

                cropper.setImageState({
                    position: newPosition,
                })

                rafIdRef.current = null
            })
        },
        [cropper, isLocalDragging]
    )

    const handleDragEnd = useCallback(() => {
        if (!cropper || !isLocalDragging) return

        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = null
        }

        setIsLocalDragging(false)
        dragStartRef.current = null

        cropper.setCropState({
            status: "idle",
            isDragging: false,
        })

        if (cropper.imageState.status === "loaded") {
            const zoomFactor = cropper.imageState.zoom / 100
            const dimensions = {
                width: cropper.imageState.dimensions.width * zoomFactor,
                height: cropper.imageState.dimensions.height * zoomFactor,
            }

            cropper.setRenderData({
                position: cropper.imageState.position,
                dimensions,
            })
        }
    }, [cropper, isLocalDragging])

    const handleMouseLeave = useCallback(() => {
        if (cropper?.cropState.isDragging) {
            handleDragEnd()
        }
    }, [cropper?.cropState.isDragging, handleDragEnd])

    return {
        handleDragStart,
        handleDrag,
        handleDragEnd,
        handleMouseLeave,
    }
}
